// Pieces the four tabs share, composed from the base components of the design system (../components) and the view
// models of core/screens. Styles in styles/screens.css (section "tabs"), semantic tokens only, no inline styles.
import { useEffect, useId, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import {
  activityRing,
  chipView,
  gaugeReading,
  liveShotScreen,
  ownerNote,
  parseTyped,
  readOnlyNote,
  readyEtaMin,
  restartBar,
  toastOf,
  shotDial,
  type ActionView,
  type ControlView,
  type DialView,
  type HistoryGroup,
  type LiveShotScreen,
  type NoteView,
  type ShotLineView,
  type StepOptions,
} from '../../core/index.js';
import {
  Banner,
  Button,
  Dial,
  FitLine,
  Icon,
  ProgressBar,
  Segmented,
  Select,
  Sheet,
  LaneChart,
  LinkSelect,
  Stepper,
  TitleInfo,
  Toggle,
  TopBar,
  type ButtonVariant,
  type IconName,
} from '../components/index.js';
import { formatNumber } from '../components/format.js';
import { GAUGE_END, GAUGE_START, gaugeAngle } from '../components/geometry.js';
import { useMachine } from '../machine-context.js';

// ------------------------------------------------------------------------------------------------ frame

/** A tab: the screen with its top bar, the restart bar (G4) and the read-only note while offline (FR-14). */
export function TabScreen({
  tab,
  title,
  accountButton,
  children,
}: {
  tab: string;
  title: string;
  accountButton?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={`screen tab-screen tab-${tab}`} data-testid={`tab-panel-${tab}`}>
      <TopBar title={title} right={accountButton} />
      <FrameBanners />
      {children}
      <LiveBar />
    </section>
  );
}

const MINI_R = 11;
const MINI_C = 2 * Math.PI * MINI_R;

/**
 * The machine while it does something (heats up, brews, flushes, descales), above the tab bar of the other tabs
 * (prototype "Reiter Bezug"): a small ring, what it does, the target below, the temperature at the right (one line, the
 * °C never wraps). A tap opens the machine tab. Quiet otherwise: nothing to watch while it is ready or in standby.
 */
export function LiveBar() {
  const { ctx, client, machineId } = useMachine();
  const history = useSyncExternalStore(
    client.history(machineId).subscribe,
    client.history(machineId).getSnapshot,
  );
  const liveShot = useSyncExternalStore(
    client.liveShot(machineId).subscribe,
    client.liveShot(machineId).getSnapshot,
  );
  const chip = chipView(ctx);
  const shot = liveShotScreen(ctx, liveShot, null);
  const activity = shot ? null : activityRing(ctx);
  const running = shot !== null && shot.phase !== 'done';
  const now = useTick(running || activity !== null, 250);
  if (chip.tone !== 'heat' && chip.tone !== 'brew' && chip.tone !== 'water') return null;
  const reading = gaugeReading(ctx, ctx.num('brew.setpoint') || undefined);
  const temp = reading.temp;
  const target = reading.target;
  // heating up (prototype "Reiter Bezug"): "Bereit in ~4 min" over "Heizt auf"; otherwise the state over its line
  const eta =
    chip.tone === 'heat' && reading.look === 'live'
      ? readyEtaMin(history.samples, temp, target, ctx.now)
      : null;
  const title = eta !== null ? ctx.tr('readyInCap', { n: eta }) : chip.text;
  const sub = eta !== null ? chip.text : reading.sub;
  const angle =
    typeof temp === 'number' && typeof target === 'number' ? gaugeAngle(temp, target) : GAUGE_START;
  // the small ring shows what the big one does (22.10.2026): the shot's progress, the water's, else the temperature
  const dial =
    running && shot
      ? shotDial(ctx, shot, Math.max(now, ctx.now))
      : activity
        ? activityRing(ctx, Math.max(now, ctx.now))
        : null;
  const share = dial ? dial.share : (angle - GAUGE_START) / (GAUGE_END - GAUGE_START);
  const fraction = Math.max(0.03, Math.min(1, share));
  return (
    <a className={`live-bar live-bar-${chip.tone}`} href="#maschine" data-testid="live-bar">
      <svg className="live-bar-ring" viewBox="0 0 28 28" aria-hidden="true" focusable="false">
        <circle className="live-bar-track" cx="14" cy="14" r={MINI_R} />
        <circle
          className="live-bar-value"
          cx="14"
          cy="14"
          r={MINI_R}
          strokeDasharray={`${Math.round(fraction * MINI_C * 10) / 10} ${Math.round(MINI_C * 10) / 10}`}
        />
      </svg>
      <span className="live-bar-text">
        <span className="live-bar-title">{title}</span>
        {sub && <span className="live-bar-sub">{sub}</span>}
      </span>
      {typeof temp === 'number' && (
        <span className="live-bar-value-text num">{formatNumber(ctx.lang, temp, 1)}&nbsp;°C</span>
      )}
    </a>
  );
}

/** A sub-page of a tab (Bezug → Bohnen, Verlauf): back arrow and title instead of the account button. */
export function SubScreen({
  tab,
  title,
  sub,
  info,
  onBack,
  right,
  testId,
  children,
}: {
  tab: string;
  title: string;
  /** a line under the title (a shot's time and bean) */
  sub?: ReactNode;
  /** an explanation of the page behind an (i) after the title */
  info?: { text: ReactNode; label: string; testId?: string } | null;
  onBack: () => void;
  right?: ReactNode;
  testId: string;
  children: ReactNode;
}) {
  const { ctx } = useMachine();
  return (
    <section className={`screen tab-screen tab-${tab} sub-page`} data-testid={testId}>
      <TopBar
        title={title}
        sub={sub}
        info={info}
        right={right}
        back={{ label: ctx.tr('back'), onClick: onBack }}
      />
      <FrameBanners />
      {children}
    </section>
  );
}

/**
 * Emergency stop, why nothing can be changed (only for states that last, core/calm.ts) and the restart bar: the same
 * on every tab.
 */
export function FrameBanners() {
  const { ctx } = useMachine();
  const bar = restartBar(ctx);
  const readOnly = readOnlyNote(ctx);
  return (
    <>
      {ctx.view.remoteLocked && (
        <Banner tone="warn" icon="alert" data-testid="remote-locked">
          {ctx.tr('remoteLocked')}
        </Banner>
      )}
      {readOnly && (
        <Banner icon="cloudOff" data-testid="read-only">
          {readOnly}
        </Banner>
      )}
      {bar && (
        <div className="restart-bar" data-testid="restart-bar">
          <span>{bar.text}</span>
          <ActionButton action={bar.restart} variant="primary" testId="restart-now" />
        </div>
      )}
    </>
  );
}

/** How long a message stays: information briefly, a failure long enough to read what to do (or until dismissed). */
const TOAST_MS = { info: 4000, error: 10_000 } as const;

/**
 * The message after a command (G5) above the tab bar, only when the user must know something (core/screens toastOf):
 * "wirkt nach dem Bezug/einem Neustart", or a failure in plain words with what to do. Success is quiet: the value or
 * the state shows it (19.10.2026). A failure can be dismissed.
 */
export function CommandToast() {
  const { client, ctx, machineId } = useMachine();
  const [toast, setToast] = useState<{ text: string; tone: 'info' | 'error'; n: number } | null>(null);
  const lang = ctx.lang;
  useEffect(
    () =>
      client.commands.onFinal((machine, input, outcome) => {
        if (machine !== machineId) return;
        const x = toastOf(lang, input, outcome);
        if (x) setToast((old) => ({ ...x, n: (old?.n ?? 0) + 1 }));
      }),
    [client, machineId, lang],
  );
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), TOAST_MS[toast.tone]);
    return () => clearTimeout(id);
  }, [toast]);
  return (
    <div className={toast ? `toast toast-${toast.tone}` : 'toast is-empty'} role="status" aria-live="polite">
      {toast?.tone === 'error' && <Icon name="alert" size="sm" />}
      <span data-testid="toast">{toast?.text ?? ''}</span>
      {toast?.tone === 'error' && (
        <button
          type="button"
          className="toast-close"
          aria-label={ctx.tr('dismiss')}
          onClick={() => setToast(null)}
          data-testid="toast-close"
        >
          <Icon name="close" size="sm" />
        </button>
      )}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ buttons

/**
 * A machine action from a view model: disabled while not allowed or open (AD-12); a safety question (catalog confirm
 * or the screen's question) as an alert sheet first. Test IDs: testId, testId-confirm, testId-cancel.
 */
export function ActionButton({
  action,
  variant = 'secondary',
  size = 'sm',
  block = false,
  icon,
  play,
  testId,
  onSent,
}: {
  action: ActionView;
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  block?: boolean;
  icon?: IconName;
  /** a round play button at the end of a programme's row; the string is its name for screen readers */
  play?: string;
  testId: string;
  /** called when the command goes out (after the safety question) */
  onSent?: () => void;
}) {
  const { send } = useMachine();
  const [asking, setAsking] = useState(false);
  const titleId = useId();
  const run = () => {
    setAsking(false);
    onSent?.();
    void send(action.input);
  };
  const ask = asking && action.ask && (
    <AskSheet ask={action.ask} titleId={titleId} onYes={run} onNo={() => setAsking(false)} testId={testId} />
  );
  if (play)
    return (
      <>
        <button
          type="button"
          className="play"
          aria-label={play}
          title={play}
          aria-busy={action.busy || undefined}
          disabled={!action.enabled}
          onClick={() => (action.ask ? setAsking(true) : run())}
          data-testid={testId}
        >
          {action.busy ? <span className="spinner spinner-sm" aria-hidden="true" /> : <Icon name="play" />}
        </button>
        {ask}
      </>
    );
  return (
    <>
      <Button
        variant={variant}
        size={size}
        block={block}
        icon={icon}
        busy={action.busy}
        disabled={!action.enabled}
        onClick={() => (action.ask ? setAsking(true) : run())}
        data-testid={testId}
      >
        {action.label}
      </Button>
      {ask}
    </>
  );
}

/** A safety question (alert sheet): the action's label confirms, Abbrechen cancels. */
export function AskSheet({
  ask,
  titleId,
  onYes,
  onNo,
  testId,
}: {
  ask: { title: string; text: string; ok: string; danger: boolean };
  titleId: string;
  onYes: () => void;
  onNo: () => void;
  testId: string;
}) {
  const { ctx } = useMachine();
  return (
    <Sheet variant="alert" title={ask.title} titleId={titleId} onClose={onNo} testId={`${testId}-sheet`}>
      <p className="text-dim">{ask.text}</p>
      <div className="sheet-actions">
        <Button
          variant={ask.danger ? 'danger' : 'primary'}
          block
          onClick={onYes}
          data-testid={`${testId}-confirm`}
        >
          {ask.ok}
        </Button>
        <Button variant="secondary" block onClick={onNo} data-testid={`${testId}-cancel`}>
          {ctx.tr('cancel')}
        </Button>
      </div>
    </Sheet>
  );
}

// ------------------------------------------------------------------------------------------------ rows and controls

/**
 * Label (and hint) left, control right; `stack` puts the control below (choices, wide fields). `info` is the row's
 * explanation behind an (i) after the label (boards: no hint line next to a stepper).
 */
export function Row({
  label,
  hint,
  info,
  stack = false,
  className,
  children,
  testId,
}: {
  label: ReactNode;
  hint?: ReactNode;
  info?: ReactNode;
  stack?: boolean;
  className?: string;
  children?: ReactNode;
  testId?: string;
}) {
  const { ctx } = useMachine();
  const text = <span className="row-label">{label}</span>;
  return (
    <div
      className={['row', stack ? 'row-stack' : '', className ?? ''].filter(Boolean).join(' ')}
      data-testid={testId}
    >
      <div className="row-text">
        {info ? (
          <TitleInfo info={info} label={ctx.tr('infoShow')} testId={testId ? `${testId}-info` : undefined}>
            {text}
          </TitleInfo>
        ) : (
          text
        )}
        {hint && <span className="hint">{hint}</span>}
      </div>
      {children !== undefined && <div className="row-control">{children}</div>}
    </div>
  );
}

/**
 * "Nur der Besitzer kann das ändern." once above a page's values for a member (23.10.2026: the sentence at every row
 * made each hint wrap); nothing for the owner.
 */
export function OwnerNote({ controls }: { controls: readonly (ControlView | null | undefined)[] }) {
  const { ctx } = useMachine();
  const text = ownerNote(ctx, controls);
  return text ? (
    <p className="hint owner-note" data-testid="owner-note">
      {text}
    </p>
  ) : null;
}

/** Buttons for a few choices (one pressed). */
export function Choice<T extends string | number>({
  label,
  options,
  onPick,
  disabled = false,
  multi = false,
  testId,
}: {
  label: string;
  options: readonly { value: T; label: string; on: boolean; hint?: string }[];
  onPick: (value: T) => void;
  disabled?: boolean;
  /** several at once (taste tags): a tap on a chosen one takes it back */
  multi?: boolean;
  testId?: string;
}) {
  return (
    <div className="choice" role="group" aria-label={label} data-testid={testId}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          className="choice-item"
          aria-pressed={o.on}
          disabled={disabled}
          title={o.hint}
          onClick={() => (multi || !o.on) && onPick(o.value)}
          data-testid={testId ? `${testId}-${o.value}` : undefined}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A text that is sent when the field is left or Enter is pressed (grind, grinder, host name). */
export function TextCommit({
  id,
  label,
  value,
  onCommit,
  disabled,
  maxLength,
  placeholder,
  hideLabel = false,
  info,
  testId,
}: {
  id: string;
  label: string;
  value: string;
  onCommit: (v: string) => void;
  disabled?: boolean;
  maxLength?: number;
  placeholder?: string;
  hideLabel?: boolean;
  /** an explanation behind an (i) after the label (board "System": "Gerätename (i)") */
  info?: ReactNode;
  testId?: string;
}) {
  const { ctx } = useMachine();
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft !== null && draft.trim() !== value) onCommit(draft.trim());
    setDraft(null);
  };
  const labelEl = (
    <label className={hideLabel ? 'visually-hidden' : 'field-label'} htmlFor={id}>
      {label}
    </label>
  );
  return (
    <div className="field">
      {info && !hideLabel ? (
        <TitleInfo info={info} label={ctx.tr('infoShow')} testId={testId ? `${testId}-info` : undefined}>
          {labelEl}
        </TitleInfo>
      ) : (
        labelEl
      )}
      <input
        id={id}
        className="field-input"
        value={draft ?? value}
        disabled={disabled}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        data-testid={testId}
      />
    </div>
  );
}

/**
 * A catalog value as a row (view model ControlView): −/+ (taps collected, one absolute value, AD-11), a switch, a
 * choice, a text. The explanation is behind an (i) after the label, a hint (one short line) under it. `bare`: the
 * control alone, its label for screen readers only, in a sheet whose title already names it (23.10.2026: "Uhr im
 * Standby | Uhr im Standby"). Test IDs: testId (row), testId-minus/-plus/-value, testId-switch, testId-<option>,
 * testId-text.
 */
export function ControlRow({
  control,
  options,
  bare = false,
  testId,
}: {
  control: ControlView | null;
  options?: StepOptions;
  bare?: boolean;
  testId?: string;
}) {
  const { ctx, stepper, send } = useMachine();
  const inputId = useId();
  if (!control) return null;
  const id = testId ?? `control-${control.id}`;
  const step: StepOptions = {
    ...(control.zeroStart !== null ? { zeroStart: control.zeroStart } : {}),
    ...options,
  };
  const set = (value: boolean | number | string) =>
    void send({ kind: 'set', params: { [control.id]: value } });
  const hint = control.hint && <span className="hint">{control.hint}</span>;
  switch (control.kind) {
    case 'stepper': {
      const stepperEl = (
        <Stepper
          value={control.text}
          provisional={control.provisional || stepper.pending(control.id) !== null}
          disabled={!control.editable}
          onMinus={() => stepper.step(control.id, -1, step)}
          onPlus={() => stepper.step(control.id, 1, step)}
          minusLabel={`${control.label} −`}
          plusLabel={`${control.label} +`}
          minusTestId={`${id}-minus`}
          plusTestId={`${id}-plus`}
          valueTestId={`${id}-value`}
          // a typed value, as precise as the control shows it (D-G04, stepCtl() of the local app)
          onType={(text) => {
            const v = parseTyped(text, control.decimals);
            if (Number.isFinite(v)) stepper.set(control.id, v, step);
          }}
          editValue={control.editText ?? ''}
          typeLabel={`${control.label}: ${ctx.tr('typeValue')}`}
        />
      );
      if (bare)
        return (
          <div className="row-bare" data-testid={id}>
            {stepperEl}
            {hint}
          </div>
        );
      return (
        <Row
          label={control.label}
          hint={control.hint}
          info={control.info}
          className="row-stepper"
          testId={id}
        >
          {stepperEl}
        </Row>
      );
    }
    case 'switch':
      return (
        <div className="row-toggle" data-testid={id}>
          <Toggle
            id={inputId}
            label={control.label}
            hint={control.hint}
            info={control.info}
            infoLabel={ctx.tr('infoShow')}
            infoTestId={`${id}-info`}
            hideLabel={bare}
            checked={control.value === true}
            disabled={!control.editable}
            onChange={(e) => set(e.target.checked)}
            data-testid={`${id}-switch`}
          />
        </div>
      );
    case 'choice': {
      const segments = segmentable(control.options);
      // more than four or long options: the chosen one as the row's value with the native list over it (board
      // "System": "Protokoll · INFO ⌄"), the row stays one line
      if (!segments && !bare)
        return (
          <Row label={control.label} hint={control.hint} info={control.info} testId={id}>
            <LinkSelect
              label={control.label}
              text={control.text}
              value={typeof control.value === 'number' ? control.value : ''}
              disabled={!control.editable}
              onChange={(e) => set(Number(e.target.value))}
              data-testid={`${id}-select`}
            >
              {control.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </LinkSelect>
          </Row>
        );
      const choice = segments ? (
        <Segmented
          label={control.label}
          options={control.options}
          disabled={!control.editable}
          onPick={(v) => set(v)}
          testId={id}
        />
      ) : (
        <Select
          label={control.label}
          value={typeof control.value === 'number' ? control.value : ''}
          disabled={!control.editable}
          onChange={(e) => set(Number(e.target.value))}
          data-testid={`${id}-select`}
        >
          {control.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      );
      if (bare)
        return (
          <div className="row-bare is-wide" data-testid={id}>
            {choice}
            {hint}
          </div>
        );
      return (
        <Row label={control.label} hint={control.hint} info={control.info} stack testId={id}>
          {choice}
        </Row>
      );
    }
    case 'text':
      return (
        <div className="row row-stack" data-testid={id}>
          <TextCommit
            id={inputId}
            label={control.label}
            info={control.info}
            value={typeof control.value === 'string' ? control.value : ''}
            disabled={!control.editable}
            maxLength={control.maxLength ?? undefined}
            onCommit={(v) => set(v)}
            testId={`${id}-text`}
          />
          {hint}
        </div>
      );
  }
}

/**
 * An on/off value as the switch alone, in the header of its sheet (the sheet's title is its label; 23.10.2026: "Warm
 * halten" went off only by tapping − down to "aus"): a switch control as it is; a stepper whose 0 is "aus" (keeping
 * warm) goes on at its zeroStart and off at 0. Test ID testId-switch.
 */
export function ControlSwitch({ control, testId }: { control: ControlView | null; testId: string }) {
  const { send, stepper } = useMachine();
  const id = useId();
  if (!control) return null;
  const pending = stepper.pending(control.id);
  const on =
    control.kind === 'switch'
      ? control.value === true
      : (pending ?? (typeof control.value === 'number' ? control.value : 0)) > 0;
  return (
    <Toggle
      id={id}
      label={control.label}
      hideLabel
      checked={on}
      disabled={!control.editable}
      onChange={(e) =>
        void send({
          kind: 'set',
          params: {
            [control.id]:
              control.kind === 'switch'
                ? e.target.checked
                : e.target.checked
                  ? (control.zeroStart ?? control.step)
                  : 0,
          },
        })
      }
      data-testid={`${testId}-switch`}
    />
  );
}

/**
 * Segments for two to four short options (UX line "Segmente"); more, or a label too long for its segment at 360 px
 * ("höher = feiner"), is a select.
 */
export function segmentable(options: readonly { label: string }[]): boolean {
  const max = options.length <= 2 ? 16 : options.length === 3 ? 11 : 9;
  return options.length >= 2 && options.length <= 4 && options.every((o) => o.label.length <= max);
}

/**
 * A note of the machine with its button (rinse, drip tray emptied, next descaling step). A note that asks for something
 * is a card of its own in the container colour (prototype "Maschine: bereit"): an icon in a circle, the text (for the
 * drip tray a title, the amount and how full it is), the button over the whole width. A note without a button is a
 * quiet banner.
 */
export function NoteRow({ note }: { note: NoteView }) {
  const icon: IconName = note.key === 'dripDue' ? 'tray' : note.tone === 'water' ? 'drop' : 'alert';
  if (!note.action)
    return (
      <div className="note-row" data-testid={`note-${note.key}`}>
        <Banner tone={note.tone === 'warn' ? 'warn' : 'info'} icon={icon}>
          {note.text}
        </Banner>
      </div>
    );
  return (
    <div
      className={note.tone === 'warn' ? 'note-card is-warn' : 'note-card'}
      data-testid={`note-${note.key}`}
    >
      <div className="note-card-body">
        <span className="note-card-icon" aria-hidden="true">
          <Icon name={icon} size="md" />
        </span>
        {note.amount ? (
          <div className="note-card-text">
            <p className="note-card-title">{note.amount.title}</p>
            <p className="note-card-sub">{note.amount.text}</p>
            <Bar fraction={note.amount.fraction} label={note.text} />
          </div>
        ) : (
          <p className="note-card-text">{note.text}</p>
        )}
      </div>
      <ActionButton action={note.action} block testId={`note-${note.key}-action`} />
    </div>
  );
}

/** Progress of a care item (backflush, descaling, drip tray): the soft bar of the design system. */
export function Bar({ fraction, due = false, label }: { fraction: number; due?: boolean; label?: string }) {
  return <ProgressBar fraction={fraction} due={due} {...(label ? { label } : {})} />;
}

// ------------------------------------------------------------------------------------------------ curves

/**
 * The running shot (prototype "Bezug läuft"): the ring fills towards the target, the centre counts whole seconds with
 * the weight "von 36 g" below; the steps pre-infusion, brewing, done under it; a card with the brew temperature and the
 * curve. After it (prototype "Bezug fertig") the closed green ring with the weight, the time and the ratio, and one
 * plain line.
 */
/** Re-renders about every 100 ms while `on`: the running shot's ring and seconds between two live values. */
function useTick(on: boolean, everyMs = 100): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [on, everyMs]);
  return now;
}

/**
 * A dial that never goes back within a run: a value that comes late would pull the estimate back a little (the ring
 * ran on, liveEstimate), so the last frame stays until the new one is ahead again (Dominik 21.10.2026: evenly).
 */
function useForward(view: DialView | null): DialView | null {
  const last = useRef<DialView | null>(null);
  const prev = last.current;
  const back =
    view !== null &&
    prev !== null &&
    prev.kind === view.kind &&
    prev.label === view.label &&
    ((view.share < prev.share && prev.share - view.share < 0.1) ||
      (view.clock !== undefined &&
        prev.clock !== undefined &&
        view.clock < prev.clock &&
        prev.clock - view.clock < 1));
  if (!back) last.current = view;
  return back ? prev : view;
}

/**
 * The ring of the shot as the round display draws it (core/screens/dial.ts shotDial, components/Dial.tsx): time and
 * weight run on every frame from the last value. Its own component, so only the ring re-renders that often.
 */
function LiveRing({ screen, testId }: { screen: LiveShotScreen; testId: string }) {
  const { ctx } = useMachine();
  const done = screen.phase === 'done';
  const now = useTick(!done);
  const dial = useForward(shotDial(ctx, screen, Math.max(now, ctx.now)));
  return <div className="machine-gauge">{dial && <Dial view={dial} testId={testId} />}</div>;
}

/**
 * The ring while water runs without a shot or the tank is empty (core/screens/activity.ts, as the round display): the
 * rinse after a shot and a single pulse as a stopwatch, a flush's pulses, a backflush's cycles and the descaling
 * rounds as segments. It runs on between two values like the shot's ring and never goes back within a run.
 */
export function ActivityDial({ testId }: { testId: string }) {
  const { ctx } = useMachine();
  const first = activityRing(ctx);
  const moving =
    first !== null && (first.kind === 'rinse' || first.kind === 'flush' || first.kind === 'descale');
  const now = useTick(moving);
  const dial = useForward(activityRing(ctx, Math.max(now, ctx.now)) ?? first);
  if (!dial) return null;
  return (
    <div className="machine-gauge" data-testid={testId} data-kind={dial.kind}>
      <Dial view={dial} testId={testId} />
    </div>
  );
}

export function LiveShot({ screen, testId }: { screen: LiveShotScreen; testId: string }) {
  const { ctx } = useMachine();
  const done = screen.phase === 'done';
  return (
    <div className="live-shot" data-testid={testId}>
      <LiveRing screen={screen} testId={testId} />
      {done ? (
        <p className="live-done">{ctx.tr('shotDoneLine')}</p>
      ) : (
        <ol className={`live-steps is-${screen.steps.length}`} data-testid={`${testId}-steps`}>
          {screen.steps.map((s) => (
            <li key={s.key} className={s.current ? 'is-current' : undefined}>
              <ProgressBar fraction={s.fraction} />
              {s.label}
            </li>
          ))}
        </ol>
      )}
      {screen.switchBack && <Banner tone="warn">{screen.switchBack}</Banner>}
      {/* the card stands from the first frame of the shot: before the first values its room is kept, so it no longer
          pushes in a quarter of a second after the start (22.10.2026) */}
      {!done && (
        <div className="card live-chart" data-testid={`${testId}-chart`}>
          <div className="live-chart-head">
            <span className="label-dim">{ctx.tr('brewTempLive')}</span>
            {screen.tempText && (
              <span className="live-chart-temp num">
                {screen.tempText}
                <span className="unit"> °C</span>
              </span>
            )}
          </div>
          {screen.lanes ? (
            <LaneChart view={screen.lanes} />
          ) : (
            <div className="live-chart-wait" aria-hidden="true" />
          )}
        </div>
      )}
    </div>
  );
}

/** "29\u00a0s" -> ["29", "s"]: the number and its unit apart, so the unit can be smaller (board "Verlauf") */
const splitUnit = (text: string): [string, string] => {
  const i = text.search(/[\s\u00a0][^\s\u00a0]+$/);
  return i > 0 ? [text.slice(0, i), text.slice(i + 1)] : [text, ''];
};

/**
 * Shots by day (prototype "Reiter Bezug" and "Verlauf"): a quiet heading per day ("Heute", "Gestern",
 * "Sonntag, 18.10."), then one line per shot: the time of day, brew time and weight, the deviation only when it missed
 * its target, the taste at the right (green when it was good), a chevron. The line never wraps (WRITING.md 0a): a
 * deviation that has no room drops out, the shot's page has it. `two` (board "Verlauf"): two lines, the figures with
 * small units over the time of day and how far it was off, the taste as a pill. While comparing, a box instead of the
 * chevron and the row toggles it.
 */
export function ShotDays({
  groups,
  onOpen,
  choosing,
  chosen = [],
  onToggle,
  rowTestId,
  two = false,
}: {
  groups: readonly HistoryGroup[];
  onOpen: (id: string) => void;
  choosing?: boolean;
  chosen?: readonly string[];
  onToggle?: (id: string) => void;
  rowTestId: (id: string) => string;
  two?: boolean;
}) {
  return (
    <>
      {groups.map((g) => (
        <section key={g.day} className="history-day" aria-label={g.title}>
          <h3 className="label day-label">{g.title}</h3>
          <ul className="list">
            {g.items.map((line) => {
              const on = chosen.includes(line.id);
              const end = choosing ? (
                <span className={on ? 'check-box compare-box is-on' : 'check-box compare-box'} />
              ) : (
                <Icon name="chevronRight" size="sm" className="list-chevron" />
              );
              const press = () => (choosing ? onToggle?.(line.id) : onOpen(line.id));
              if (two) {
                const [tn, tu] = splitUnit(line.timeShort);
                const [wn, wu] = line.weightText ? splitUnit(line.weightText) : ['', ''];
                return (
                  <li key={line.id} className="list-block">
                    <button
                      type="button"
                      className={choosing ? 'shot-row2 is-choosing' : 'shot-row2'}
                      aria-pressed={choosing ? on : undefined}
                      onClick={press}
                      data-testid={rowTestId(line.id)}
                    >
                      <span className="shot2-text">
                        <span className="shot2-figures num">
                          <span>
                            <b>{tn}</b>
                            <small>{tu}</small>
                          </span>
                          {wn && (
                            <span>
                              <b>{wn}</b>
                              <small>{wu}</small>
                            </span>
                          )}
                        </span>
                        <FitLine
                          className="hint shot2-sub"
                          parts={[
                            <span key="c" className="num">
                              {line.clock}
                            </span>,
                            line.offText && (
                              <span key="o" className="shot2-off">
                                {line.offText}
                              </span>
                            ),
                          ]}
                        />
                      </span>
                      {line.taste && (
                        <span className={line.taste.good ? 'chip shot2-taste is-good' : 'chip shot2-taste'}>
                          {line.taste.text}
                        </span>
                      )}
                      {end}
                    </button>
                  </li>
                );
              }
              return (
                <li key={line.id} className="list-block">
                  <button
                    type="button"
                    className={choosing ? 'shot-row is-choosing' : 'shot-row'}
                    aria-pressed={choosing ? on : undefined}
                    onClick={press}
                    data-testid={rowTestId(line.id)}
                  >
                    <span className="shot-clock num">{line.clock}</span>
                    <b className="shot-t num">{line.timeShort}</b>
                    {/* the weight, the deviation beside it while there is room (one line high, it drops out else;
                        23.10.2026: "+2,0 g" went alone under "38,0 g" next to "ausgewogen"); without a scale the
                        place stays empty, no "–" */}
                    <span className="shot-wcell">
                      {line.weightText && <b className="shot-w num">{line.weightText}</b>}
                      {line.deviation?.off && <span className="shot-off num">{line.deviation.text}</span>}
                    </span>
                    {line.taste && (
                      <span className={line.taste.good ? 'shot-taste is-good' : 'shot-taste'}>
                        {line.taste.text}
                      </span>
                    )}
                    {end}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </>
  );
}

/**
 * A shot in a list (Letzte Bezüge, Verlauf): time and weight, the deviation only when the shot missed its target, the
 * taste at the right, one line under them. The row's button and its chevron or compare box are the caller's.
 */
export function ShotLineBody({ line, sub }: { line: ShotLineView; sub: string }) {
  return (
    <>
      <span className="shot-main num">
        <b className="shot-t">{line.timeShort}</b>
        {line.weightText && <b className="shot-w">{line.weightText}</b>}
        {line.deviation?.off && <small className="dev is-off">{line.deviation.text}</small>}
      </span>
      {line.taste && (
        <span className={line.taste.good ? 'shot-taste is-good' : 'shot-taste'}>{line.taste.text}</span>
      )}
      {sub && <small className="hint shot-sub">{sub}</small>}
    </>
  );
}
